import type { CarTireTreadDepthLatestResponseRecord } from '@navio/nsdk';
import { TireSeason } from '@navio/nsdk';
import type { CarWheelMeasurement, WheelSetTireMeasurementV2 } from 'noddi-types';
import { type TreadDepthThresholds, WHEEL_FIELDS, type WheelField, wheelFieldFromLocation } from 'noddi-util';
import type { SeasonMeasurementMap, WheelMeasurementValue } from '../../../Inspection/inspectionSessions';
import { WheelSetStatus, WheelSetStatusType } from '../../types';
import { getOverallWheelSetStatus, treadDepthWheelSetStatus } from '../../wheelSetStatus';

/** Four wheel positions as returned by booking-item inspection measurements. */
export type WheelSetCarWheelMeasurementValues = Record<WheelField, WheelMeasurementValue>;

/** OpenAPI map for summer/winter on a booking-item inspection, or embedded V2 wheel set. */
type WheelSetMeasurementsCandidate = WheelSetTireMeasurementV2 | SeasonMeasurementMap;

type WheelPositionMeasurement = CarWheelMeasurement | WheelMeasurementValue;

function hasWheelPositionMeasurement(value: WheelPositionMeasurement | undefined): value is WheelPositionMeasurement {
  return value != null;
}

/**
 * True when all four wheel positions are present. Necessary because the booking-item API types
 * summer/winter as string-keyed maps; the UI card needs every corner.
 */
export function isWheelSetCarWheelMeasurementValues(
  m: WheelSetMeasurementsCandidate | null | undefined
): m is WheelSetCarWheelMeasurementValues | WheelSetTireMeasurementV2 {
  if (m == null) {
    return false;
  }
  return WHEEL_FIELDS.every((key) => hasWheelPositionMeasurement(m[key]));
}

/**
 * Maps {@link CarTireTreadDepthLatestResponseRecord} (NSDK `GET .../tire-tread-depth-latest/`) into the
 * four-corner shape expected by {@link TireMeasurementCard}.
 */
export function wheelSetMeasurementsFromTreadDepthLatest(
  response: CarTireTreadDepthLatestResponseRecord | undefined,
  tireSeason: TireSeason
): WheelSetCarWheelMeasurementValues | undefined {
  if (!response?.seasons?.length) {
    return undefined;
  }

  const seasonRecord = response.seasons.find((s) => s.season === tireSeason);
  const positions = seasonRecord?.positions;
  if (!positions?.length) {
    return undefined;
  }

  const byPosition: Partial<Record<WheelField, WheelMeasurementValue>> = {};

  // Positions we do not inspect (inner wheels, middle axles) have no corner to fill.
  for (const position of positions) {
    const key = wheelFieldFromLocation(position.position);
    if (!key) {
      continue;
    }
    byPosition[key] = {
      measuredAt: position.measuredAt,
      value: {
        unit: position.value.unit,
        value: position.value.value
      }
    };
  }

  const { frontLeft, frontRight, rearLeft, rearRight } = byPosition;
  if (!frontLeft || !frontRight || !rearLeft || !rearRight) {
    return undefined;
  }

  return { frontLeft, frontRight, rearLeft, rearRight };
}

/** The season's legal and recommended minimum in the car's country; null where neither is regulated. */
export function treadDepthThresholdsFromLatest(
  response: CarTireTreadDepthLatestResponseRecord | undefined,
  tireSeason: TireSeason
): TreadDepthThresholds | null {
  return response?.seasons.find((s) => s.season === tireSeason)?.treadDepthThresholds ?? null;
}

export function wheelSetStatusFromTreadDepthLatest(
  response: CarTireTreadDepthLatestResponseRecord | undefined,
  tireSeason: TireSeason
): WheelSetStatusType {
  const measurements = wheelSetMeasurementsFromTreadDepthLatest(response, tireSeason);
  const thresholds = treadDepthThresholdsFromLatest(response, tireSeason);
  if (!measurements || !thresholds) {
    return WheelSetStatus.MissingMeasurements;
  }

  return getOverallWheelSetStatus(
    [measurements.frontLeft, measurements.frontRight, measurements.rearLeft, measurements.rearRight].map((wheel) =>
      treadDepthWheelSetStatus(wheel.value.value, thresholds)
    )
  );
}
